import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Cpu, RefreshCw, PlayCircle, ChevronDown, ChevronRight,
  ShieldCheck, Settings2, Loader2, ShieldAlert, RotateCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'prev_month', label: 'Previous month' },
];

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtCost = (n, cur) => {
  const v = Number(n || 0);
  const sym = cur === 'INR' ? '\u20B9' : (cur === 'USD' ? '$' : '');
  return `${sym}${v.toLocaleString('en-US', { minimumFractionDigits: v < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
};
const fmtTime = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '\u2014' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const STATUS_META = {
  success: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'success' },
  failure: { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'failure' },
  blocked: { cls: 'bg-amber-50 text-amber-800 border-amber-200', label: 'blocked' },
};

// ── Small dense stat card ────────────────────────────────────────────────
function Stat({ label, value, sub, testid }) {
  return (
    <div className="border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-lg font-semibold text-slate-900 tabular-nums font-mono mt-0.5">{value}</p>
      {sub ? <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p> : null}
    </div>
  );
}

// ── Option renderers (module-level; map only over identifier arrays) ───────
function OptionItem({ v }) { return <option value={v}>{v}</option>; }
function DataOption({ v }) { return <option value={v} />; }

// ── Attempt-log line ──────────────────────────────────────────────────────
function AttemptLine({ a }) {
  const tone = a.status === 'success' ? 'text-emerald-700' : (a.status === 'retrying' ? 'text-indigo-700' : 'text-rose-700');
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-400">#{a.attempt}</span>
      <span className={tone}>{a.status}</span>
      <span className="text-slate-400">{a.latencyMs}ms</span>
      {a.error ? <span className="text-rose-600 truncate">{a.error}</span> : null}
    </div>
  );
}

// ── Recent-call row (module-level to keep the visual-edits babel plugin safe) ─
function CallRow({ r, currency }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[r.status] || { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: r.status || '\u2014' };
  const log = r.attemptLog || [];
  const hasLog = log.length > 0;
  return (
    <>
      <TableRow className="hover:bg-slate-50 cursor-pointer" onClick={() => hasLog && setOpen((o) => !o)} data-testid={`ai-call-row-${r.id}`}>
        <TableCell className="text-xs text-slate-600 whitespace-nowrap">
          <span className="inline-flex items-center gap-1">
            {hasLog ? (open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />) : <span className="w-3" />}
            {fmtTime(r.timestamp)}
          </span>
        </TableCell>
        <TableCell className="text-xs font-medium text-slate-700">{r.feature || '\u2014'}</TableCell>
        <TableCell className="text-xs text-slate-500 font-mono">{r.provider}/{r.model}</TableCell>
        <TableCell className="text-xs text-slate-500">{r.userEmail || '\u2014'}</TableCell>
        <TableCell className="text-right text-xs tabular-nums font-mono">{r.inputTokens == null ? '\u2014' : fmtNum(r.inputTokens)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums font-mono">{r.outputTokens == null ? '\u2014' : fmtNum(r.outputTokens)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums font-mono">{r.totalTokens == null ? '\u2014' : fmtNum(r.totalTokens)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums font-mono">{r.estimatedCost == null ? '\u2014' : fmtCost(r.estimatedCost, r.currency || currency)}</TableCell>
        <TableCell className="text-right text-xs tabular-nums text-slate-500">{r.latencyMs == null ? '\u2014' : `${r.latencyMs}ms`}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
            {r.retried ? <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200 gap-0.5"><RotateCw className="h-2.5 w-2.5" />retry</Badge> : null}
          </div>
        </TableCell>
      </TableRow>
      {open && hasLog ? (
        <TableRow className="bg-slate-50/60" data-testid={`ai-call-detail-${r.id}`}>
          <TableCell colSpan={10} className="text-[11px] text-slate-600">
            <div className="py-1 space-y-1">
              <div className="font-mono text-slate-400">request {r.requestId} · {r.attempts} attempt(s)</div>
              {log.map((a, i) => <AttemptLine key={i} a={a} />)}
              {r.error ? <div className="text-rose-600">{r.error}</div> : null}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

// ── Feature breakdown row ──────────────────────────────────────────────────
function FeatureRow({ row, currency }) {
  return (
    <TableRow data-testid={`ai-feature-row-${row.feature}`}>
      <TableCell className="text-xs font-medium text-slate-700">{row.feature}</TableCell>
      <TableCell className="text-right text-xs tabular-nums font-mono">{fmtNum(row.calls)}</TableCell>
      <TableCell className="text-right text-xs tabular-nums font-mono">{fmtNum(row.inputTokens)}</TableCell>
      <TableCell className="text-right text-xs tabular-nums font-mono">{fmtNum(row.outputTokens)}</TableCell>
      <TableCell className="text-right text-xs tabular-nums font-mono">{fmtNum(row.totalTokens)}</TableCell>
      <TableCell className="text-right text-xs tabular-nums font-mono">{fmtCost(row.estimatedCost, currency)}</TableCell>
    </TableRow>
  );
}

// ── Alert line ─────────────────────────────────────────────────────────────
function AlertLine({ a }) {
  return (
    <li data-testid={`ai-alert-${a.feature}`} className="font-mono">
      {a.feature}: {fmtCost(a.spend, a.currency)} spent · threshold {fmtCost(a.threshold, a.currency)} · over by {fmtCost(a.overBy, a.currency)}
    </li>
  );
}

// ── Pricing row (settings) ──────────────────────────────────────────────────
function PricingRow({ row, idx, onChange, onSave }) {
  return (
    <TableRow data-testid={`ai-pricing-row-${idx}`}>
      <TableCell className="text-xs font-mono text-slate-600">{row.provider}/{row.model}</TableCell>
      <TableCell className="text-right">
        <input type="number" step="0.01" value={row.inputCostPer1M} onChange={(e) => onChange(idx, 'inputCostPer1M', parseFloat(e.target.value))} className="w-20 text-xs text-right border border-slate-200 rounded px-1 py-0.5 font-mono" data-testid={`ai-pricing-input-${idx}`} />
      </TableCell>
      <TableCell className="text-right">
        <input type="number" step="0.01" value={row.outputCostPer1M} onChange={(e) => onChange(idx, 'outputCostPer1M', parseFloat(e.target.value))} className="w-20 text-xs text-right border border-slate-200 rounded px-1 py-0.5 font-mono" data-testid={`ai-pricing-output-${idx}`} />
      </TableCell>
      <TableCell><input value={row.currency} onChange={(e) => onChange(idx, 'currency', e.target.value.toUpperCase())} className="w-14 text-xs border border-slate-200 rounded px-1 py-0.5 font-mono" /></TableCell>
      <TableCell className="text-xs text-slate-400 font-mono">{row.effectiveDate || '\u2014'}</TableCell>
      <TableCell><Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onSave(row)} data-testid={`ai-pricing-save-${idx}`}>Save</Button></TableCell>
    </TableRow>
  );
}

// ── Budget row (settings) ───────────────────────────────────────────────────
function BudgetRow({ feat, amt, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2" data-testid={`ai-budget-row-${feat}`}>
      <span className="text-xs font-mono text-slate-600 w-48 truncate">{feat}</span>
      <input type="number" step="1" value={amt} onChange={(e) => onChange(feat, parseFloat(e.target.value))} className="w-28 text-xs text-right border border-slate-200 rounded px-2 py-1 font-mono" data-testid={`ai-budget-input-${feat}`} />
      <button type="button" className="text-[11px] text-slate-400 hover:text-rose-600" onClick={() => onRemove(feat)}>remove</button>
    </div>
  );
}

// ── Settings panel (config / pricing / budgets) ──────────────────────────
function GatewaySettings({ meta, onChanged }) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [budgetCurrency, setBudgetCurrency] = useState('USD');
  const [newBudgetFeature, setNewBudgetFeature] = useState('');
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ai/config`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/ai/pricing`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/ai/budgets`, { withCredentials: true }),
      ]);
      setConfig(c.data);
      setPricing(p.data.pricing || []);
      setBudgets(b.data.budgets || {});
      setBudgetCurrency(b.data.currency || 'USD');
    } catch {
      toast.error('Failed to load AI gateway settings');
    }
  }, []);

  useEffect(() => { if (open && !config) load(); }, [open, config, load]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await axios.put(`${BACKEND_URL}/api/ai/config`, {
        defaultProvider: config.defaultProvider,
        defaultModel: config.defaultModel,
        currency: config.currency,
      }, { withCredentials: true });
      toast.success('Default provider / model saved');
      onChanged && onChanged();
    } catch { toast.error('Save failed'); } finally { setSavingCfg(false); }
  };

  const savePricingRow = async (row) => {
    try {
      await axios.put(`${BACKEND_URL}/api/ai/pricing`, row, { withCredentials: true });
      toast.success(`Pricing saved for ${row.provider}/${row.model}`);
      load();
    } catch { toast.error('Pricing save failed'); }
  };

  const saveBudgets = async () => {
    setSavingBudget(true);
    try {
      await axios.put(`${BACKEND_URL}/api/ai/budgets`, { budgets }, { withCredentials: true });
      toast.success('Budgets saved');
      onChanged && onChanged();
    } catch { toast.error('Budget save failed'); } finally { setSavingBudget(false); }
  };

  const updatePricing = (idx, field, val) => {
    setPricing((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };
  const updateBudget = (feat, val) => setBudgets((b) => ({ ...b, [feat]: val }));
  const removeBudget = (feat) => setBudgets((b) => { const n = { ...b }; delete n[feat]; return n; });

  const knownProviders = Object.keys(meta.knownModels || {});
  const knownModelFlat = [];
  knownProviders.forEach((p) => (meta.knownModels[p] || []).forEach((m) => knownModelFlat.push(m)));
  const budgetEntries = Object.entries(budgets);
  const budgetKeys = Object.keys(budgets);
  const metaFeatures = meta.features || [];
  const featureOptions = Array.from(new Set([...metaFeatures, ...budgetKeys]));

  return (
    <Card className="border-slate-200" data-testid="ai-gateway-settings">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3" data-testid="ai-settings-toggle">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">AI Gateway Settings</span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Admin only</span>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {open && config ? (
        <div className="px-4 pb-4 space-y-5 border-t border-slate-100 pt-4">
          {/* Default provider / model / currency */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Default provider &amp; model</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-slate-500">Provider
                <input list="ai-providers" value={config.defaultProvider} onChange={(e) => setConfig({ ...config, defaultProvider: e.target.value })} className="block mt-1 text-sm border border-slate-200 rounded px-2 py-1 w-40 font-mono" data-testid="ai-config-provider" />
              </label>
              <label className="text-xs text-slate-500">Model
                <input list="ai-models" value={config.defaultModel} onChange={(e) => setConfig({ ...config, defaultModel: e.target.value })} className="block mt-1 text-sm border border-slate-200 rounded px-2 py-1 w-56 font-mono" data-testid="ai-config-model" />
              </label>
              <label className="text-xs text-slate-500">Currency
                <input value={config.currency} onChange={(e) => setConfig({ ...config, currency: e.target.value.toUpperCase() })} className="block mt-1 text-sm border border-slate-200 rounded px-2 py-1 w-20 font-mono" data-testid="ai-config-currency" />
              </label>
              <Button size="sm" onClick={saveConfig} disabled={savingCfg} className="bg-slate-800 hover:bg-slate-900" data-testid="ai-config-save">
                {savingCfg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
              </Button>
              <datalist id="ai-providers">{knownProviders.map((p) => <DataOption key={p} v={p} />)}</datalist>
              <datalist id="ai-models">{knownModelFlat.map((m) => <DataOption key={m} v={m} />)}</datalist>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Model pricing (cost per 1M tokens · historical records keep their request-time cost)</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Provider / Model</TableHead>
                  <TableHead className="text-xs text-right">Input / 1M</TableHead>
                  <TableHead className="text-xs text-right">Output / 1M</TableHead>
                  <TableHead className="text-xs">Currency</TableHead>
                  <TableHead className="text-xs">Effective</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricing.map((row, idx) => (
                  <PricingRow key={`${row.provider}/${row.model}`} row={row} idx={idx} onChange={updatePricing} onSave={savePricingRow} />
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Budgets */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Per-feature monthly budget alerts ({budgetCurrency}) — soft alert only, never blocks a call</p>
            <div className="space-y-1.5">
              {budgetEntries.length === 0 ? <p className="text-xs text-slate-400">No budgets set. Add one below.</p> : null}
              {budgetEntries.map(([feat, amt]) => (
                <BudgetRow key={feat} feat={feat} amt={amt} onChange={updateBudget} onRemove={removeBudget} />
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input list="ai-budget-features" value={newBudgetFeature} onChange={(e) => setNewBudgetFeature(e.target.value)} placeholder="feature key (e.g. vendor_intelligence)" className="text-xs border border-slate-200 rounded px-2 py-1 w-64 font-mono" data-testid="ai-budget-new-feature" />
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!newBudgetFeature.trim()} onClick={() => { updateBudget(newBudgetFeature.trim(), 0); setNewBudgetFeature(''); }} data-testid="ai-budget-add">Add</Button>
              <Button size="sm" onClick={saveBudgets} disabled={savingBudget} className="bg-slate-800 hover:bg-slate-900 h-7 text-[11px]" data-testid="ai-budget-save">
                {savingBudget ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save budgets'}
              </Button>
              <datalist id="ai-budget-features">{featureOptions.map((f) => <DataOption key={f} v={f} />)}</datalist>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export default function AiUsageDashboard() {
  const [range, setRange] = useState('month');
  const [filters, setFilters] = useState({ feature: '', provider: '', model: '', user: '' });
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ features: [], providers: [], models: [], users: [], knownModels: {} });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range };
      if (filters.feature) params.feature = filters.feature;
      if (filters.provider) params.provider = filters.provider;
      if (filters.model) params.model = filters.model;
      if (filters.user) params.user = filters.user;
      const res = await axios.get(`${BACKEND_URL}/api/ai/usage`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load AI usage');
    } finally {
      setLoading(false);
    }
  }, [range, filters]);

  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/meta`, { withCredentials: true });
      setMeta(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const runTest = async (simulate) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/test`, simulate ? { simulate } : {}, { withCredentials: true });
      setTestResult(res.data);
      const s = res.data.status;
      if (s === 'success') toast.success(`Test call OK \u00b7 ${res.data.totalTokens ?? '\u2014'} tokens`);
      else if (s === 'blocked') toast.warning('Test call blocked (sensitive data)');
      else toast.error('Test call failed \u2014 AI unavailable');
      load();
      loadMeta();
    } catch {
      toast.error('Test call request failed');
    } finally {
      setTesting(false);
    }
  };

  const t = data?.totals || {};
  const cur = data?.currency || 'USD';
  const alerts = data?.alerts || [];
  const featureRows = data?.byFeature || [];
  const records = data?.records || [];
  const metaFeatures = meta.features || [];
  const metaProviders = meta.providers || [];
  const metaModels = meta.models || [];
  const metaUsers = meta.users || [];
  const hasFilter = filters.feature || filters.provider || filters.model || filters.user;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="ai-usage-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-slate-600" />
            <h1 className="text-xl font-bold text-slate-900">AI Usage &amp; Cost</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            Every AI call routes through one server-side gateway. Sensitive data (PAN / passport / card / password) is blocked before it reaches any provider.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => runTest(null)} disabled={testing} data-testid="ai-test-call-btn">
            {testing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />} Run test call
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="ai-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Range segmented control */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="ai-range-control">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            className={`text-xs px-3 py-1.5 rounded border transition-colors ${range === r.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid={`ai-range-${r.key}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Budget alerts */}
      {alerts.length > 0 ? (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="ai-budget-alerts">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-1">
            <ShieldAlert className="h-4 w-4" /> Monthly budget exceeded
          </div>
          <ul className="text-xs text-amber-800 space-y-0.5">
            {alerts.map((a) => <AlertLine key={a.feature} a={a} />)}
          </ul>
          <p className="text-[10px] text-amber-700 mt-1">Alert only — AI calls are never blocked by a budget.</p>
        </div>
      ) : null}

      {/* Test result */}
      {testResult ? (
        <div className={`border rounded-md p-3 text-xs ${testResult.status === 'success' ? 'border-emerald-200 bg-emerald-50' : (testResult.status === 'blocked' ? 'border-amber-200 bg-amber-50' : 'border-rose-200 bg-rose-50')}`} data-testid="ai-test-result">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={STATUS_META[testResult.status]?.cls || ''}>{testResult.status}</Badge>
            <span className="font-mono text-slate-500">{testResult.provider}/{testResult.model}</span>
            <span className="text-slate-600">tokens: <span className="font-mono">{testResult.totalTokens ?? '\u2014'}</span></span>
            <span className="text-slate-600">cost: <span className="font-mono">{testResult.estimatedCost == null ? '\u2014' : fmtCost(testResult.estimatedCost, testResult.currency)}</span></span>
            <span className="text-slate-600">latency: <span className="font-mono">{testResult.latencyMs ?? '\u2014'}ms</span></span>
            {testResult.retried ? <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">retried</Badge> : null}
          </div>
          {testResult.text ? <p className="mt-1 text-slate-600">"{testResult.text}"</p> : null}
          {testResult.message ? <p className="mt-1 text-slate-500">{testResult.message}</p> : null}
          <div className="flex items-center gap-2 mt-2">
            <button type="button" className="text-[11px] text-slate-500 underline" onClick={() => runTest('transient')} data-testid="ai-test-transient">Simulate transient failure (retry)</button>
            <button type="button" className="text-[11px] text-slate-500 underline" onClick={() => runTest('unavailable')} data-testid="ai-test-unavailable">Simulate unavailable</button>
          </div>
        </div>
      ) : null}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="ai-stats">
        <Stat label="Calls" value={fmtNum(t.calls)} sub={t.failures ? `${t.failures} failed \u00b7 ${t.blocked || 0} blocked` : (t.blocked ? `${t.blocked} blocked` : null)} testid="ai-stat-calls" />
        <Stat label="Input tokens" value={fmtNum(t.inputTokens)} testid="ai-stat-input" />
        <Stat label="Output tokens" value={fmtNum(t.outputTokens)} testid="ai-stat-output" />
        <Stat label="Total tokens" value={fmtNum(t.totalTokens)} testid="ai-stat-total" />
        <Stat label={`Est. cost (${cur})`} value={fmtCost(t.estimatedCost, cur)} testid="ai-stat-cost" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="ai-filters">
        <select value={filters.feature} onChange={(e) => setFilters({ ...filters, feature: e.target.value })} className="border border-slate-200 rounded px-2 py-1 bg-white" data-testid="ai-filter-feature">
          <option value="">All features</option>
          {metaFeatures.map((f) => <OptionItem key={f} v={f} />)}
        </select>
        <select value={filters.provider} onChange={(e) => setFilters({ ...filters, provider: e.target.value })} className="border border-slate-200 rounded px-2 py-1 bg-white" data-testid="ai-filter-provider">
          <option value="">All providers</option>
          {metaProviders.map((p) => <OptionItem key={p} v={p} />)}
        </select>
        <select value={filters.model} onChange={(e) => setFilters({ ...filters, model: e.target.value })} className="border border-slate-200 rounded px-2 py-1 bg-white" data-testid="ai-filter-model">
          <option value="">All models</option>
          {metaModels.map((m) => <OptionItem key={m} v={m} />)}
        </select>
        <select value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} className="border border-slate-200 rounded px-2 py-1 bg-white" data-testid="ai-filter-user">
          <option value="">All users</option>
          {metaUsers.map((u) => <OptionItem key={u} v={u} />)}
        </select>
        {hasFilter ? (
          <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setFilters({ feature: '', provider: '', model: '', user: '' })} data-testid="ai-filter-clear">clear</button>
        ) : null}
      </div>

      {/* Feature breakdown */}
      <Card className="overflow-hidden" data-testid="ai-feature-breakdown">
        <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wide">Feature breakdown</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Feature</TableHead>
              <TableHead className="text-xs text-right">Calls</TableHead>
              <TableHead className="text-xs text-right">Input</TableHead>
              <TableHead className="text-xs text-right">Output</TableHead>
              <TableHead className="text-xs text-right">Total tokens</TableHead>
              <TableHead className="text-xs text-right">Est. cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {featureRows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-400 py-6" data-testid="ai-feature-empty">No AI calls in this period.</TableCell></TableRow>
            ) : featureRows.map((row) => <FeatureRow key={row.feature} row={row} currency={cur} />)}
          </TableBody>
        </Table>
      </Card>

      {/* Recent calls */}
      <Card className="overflow-hidden" data-testid="ai-recent-calls">
        <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center justify-between">
          <span>Recent calls</span>
          <span className="text-slate-400 normal-case font-normal">{data?.recordCount || 0} in period</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Time</TableHead>
                <TableHead className="text-xs">Feature</TableHead>
                <TableHead className="text-xs">Provider / Model</TableHead>
                <TableHead className="text-xs">User</TableHead>
                <TableHead className="text-xs text-right">In</TableHead>
                <TableHead className="text-xs text-right">Out</TableHead>
                <TableHead className="text-xs text-right">Total</TableHead>
                <TableHead className="text-xs text-right">Cost</TableHead>
                <TableHead className="text-xs text-right">Latency</TableHead>
                <TableHead className="text-xs">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-xs text-slate-400 py-6" data-testid="ai-records-empty">{loading ? 'Loading\u2026' : 'No AI calls yet. Run a test call to create the first ledger record.'}</TableCell></TableRow>
              ) : records.map((r) => <CallRow key={r.id} r={r} currency={cur} />)}
            </TableBody>
          </Table>
        </div>
      </Card>

      <GatewaySettings meta={meta} onChanged={() => { load(); loadMeta(); }} />
    </div>
  );
}
